import { PublicClientApplication } from '@azure/msal-browser';

import { AUTH_SCOPES, Constants, EXTENDED_AUTH_SCOPES } from './constants.ts';

export type GraphObject = {
  id?: string;
  name?: string;
  isFolder?: boolean;
  siteId?: string;
  driveId?: string;
  parentId?: string;
  size?: number;
};

export enum ItemType {
  Folder,
  Workbook,
}

export type GraphApiItem = {
  id?: string;
  name?: string;
  parentReference?: {
    siteId?: string;
    driveId?: string;
    id?: string;
  };
  file?: unknown;
  folder?: unknown;
  size?: number;
};

export class GraphClient {
  accessToken?: string;
  filter?: string;

  constructor(accessToken?: string, filter?: string) {
    this.accessToken = accessToken;
    this.filter = filter;
  }

  checkHasElevatedScopes() {
    try {
      if (!this.accessToken) return false;

      const jwt = this.accessToken.split('.')[1];
      const decoded = JSON.parse(atob(jwt));
      const scopesGranted = decoded['scp'].split(' ');

      const extraScopes = EXTENDED_AUTH_SCOPES.filter((scope) => !AUTH_SCOPES.includes(scope));
      return extraScopes.every((scope) => scopesGranted.includes(scope));
    } catch (err) {
      console.error(err);
    }

    return false;
  }

  async search(query: string | undefined, itemType: ItemType): Promise<GraphObject[]> {
    let hits = [];

    if (this.checkHasElevatedScopes()) {
      const filter = itemType === ItemType.Folder ? 'IsContainer:true AND IsDocument:false' : 'filetype:xlsx';
      const queryString = (query ?? '') + (filter ? ` ${filter}` : '');

      const res = await this.execute(`https://graph.microsoft.com/v1.0/search/query`, 'POST', {
        requests: [
          {
            entityTypes: ['driveItem'],
            query: { queryString },
          },
        ],
      });

      hits = res?.value?.[0]?.hitsContainers?.[0]?.hits.map((it) => it.resource);
    } else {
      const res = await this.execute(`https://graph.microsoft.com/v1.0/me/drive/root/search(q='${query}')`, 'GET');
      console.log('Res:', res);

      hits = res.value;
    }

    return this.filterByItemType(hits.map(this.toGraphObject) || [], itemType);
  }

  async getLoggedInUser() {
    return await this.execute('https://graph.microsoft.com/v1.0/me', 'GET');
  }

  async recent(itemType: ItemType): Promise<GraphObject[]> {
    if (this.checkHasElevatedScopes()) {
      return await this.search('', itemType);
    } else {
      if (itemType === ItemType.Folder) {
        const res = await this.execute(
          'https://graph.microsoft.com/v1.0/me/drive/root/children?$filter=folder ne null',
          'GET'
        );

        return this.filterByItemType(res.value.map(this.toGraphObject) || [], itemType);
      } else {
        const res = await this.execute('https://graph.microsoft.com/v1.0/me/drive/recent', 'GET');
        return this.filterByItemType(res.value.map(this.toGraphObject) || [], itemType);
      }
    }
  }

  filterByItemType(objects: GraphObject[], itemType: ItemType): GraphObject[] {
    return objects.filter((obj) => {
      if (itemType === ItemType.Folder) {
        return obj.isFolder;
      } else if (itemType === ItemType.Workbook) {
        return obj.name?.endsWith('xlsx');
      }
    });
  }

  async get(driveId: string, id: string): Promise<GraphObject> {
    const res = await this.execute(`https://graph.microsoft.com/v1.0/drives/${driveId}/items/${id}`, 'GET');

    return this.toGraphObject({
      ...res,
      parentReference: {
        ...res.parentReference,
        siteId: undefined,
      },
    });
  }

  async getWorksheets(siteId: string | undefined, driveId: string, itemId: string): Promise<GraphObject[]> {
    const res = await this.execute(
      `https://graph.microsoft.com/v1.0/${this.getDriveBaseUrl(siteId)}/${driveId}/items/${itemId}/workbook/worksheets`,
      'GET'
    );

    return res?.value;
  }

  async getTables(
    siteId: string | undefined,
    driveId: string,
    itemId: string,
    worksheetId: string
  ): Promise<GraphObject[]> {
    const res = await this.execute(
      `https://graph.microsoft.com/v1.0/${this.getDriveBaseUrl(siteId)}/${driveId}/items/${itemId}/workbook/worksheets/${worksheetId}/tables`,
      'GET'
    );

    return res?.value;
  }

  async getTableColumns(
    siteId: string | undefined,
    driveId: string,
    itemId: string,
    tableId: string
  ): Promise<GraphObject[]> {
    const res = await this.execute(
      `https://graph.microsoft.com/v1.0/${this.getDriveBaseUrl(siteId)}/${driveId}/items/${itemId}/workbook/tables/${tableId}/columns`,
      'GET'
    );

    return res?.value;
  }

  async execute(endpoint: string, method: string, payload?: object) {
    if (!this.accessToken) return {};

    const headers = new Headers({
      Authorization: `Bearer ${this.accessToken}`,
      ...(payload ? { 'Content-Type': 'application/json' } : {}),
    });

    const result = await fetch(endpoint, { method, headers, body: JSON.stringify(payload) });

    try {
      return result.json();
    } catch (e) {
      console.error(e);
      return {};
    }
  }

  getDriveBaseUrl(siteId?: string): string {
    if (siteId && this.checkHasElevatedScopes()) {
      return `sites/${siteId}/drives`;
    } else {
      return 'me/drives';
    }
  }

  toGraphObject(obj: GraphApiItem): GraphObject {
    return {
      id: obj.id,
      name: obj.name,
      siteId: obj.parentReference?.siteId,
      driveId: obj.parentReference?.driveId,
      parentId: obj.parentReference?.id,
      isFolder: obj.file == null || obj.folder != null,
      size: obj.size,
    };
  }
}

export const msalInstance = new PublicClientApplication({
  auth: {
    clientId: Constants.ClientId?.toString(),
    authority: Constants.Authority?.toString(),
    redirectUri: Constants.RedirectUri?.toString(),
  },
  cache: {
    cacheLocation: 'sessionStorage',
    storeAuthStateInCookie: false,
  },
  system: {
    loggerOptions: {
      loggerCallback: console.log,
    },
  },
});

export const basicLoginRequest = { scopes: AUTH_SCOPES };
export const extendedLoginRequest = { scopes: EXTENDED_AUTH_SCOPES };
